import { Suspense } from "react";
import { Await } from "react-router";

import type { BuilderContent } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { PrefetchedBuilderContent } from "~/hec/components/prefetched-builder-content";

import { FeatureContentModules } from "./feature-content-modules";

/**
 * The second PDP Inline Content position (COM-793): the entry's
 * `belowAccordions` region, below the product accordions — SFRA's "Featured
 * Content Module 2" placement. Same entry, targeting and schedule as the
 * above-accordions slot; only the region differs.
 *
 * Precedence, top to bottom:
 *
 *  1. `region` set → the Builder region. Keyed by its (region-scoped) id so a
 *     PDP→PDP soft nav remounts `<Content>` instead of reusing it across
 *     entries. Rendered with `canTrack={false}` — REQUIRED, see
 *     `scopeRegionId`: with tracking on, Builder serves A/B variants whose
 *     `data.blocks` is the MAIN canvas (copying the above-accordions content
 *     down here) and logs a second impression per view.
 *  2. No region but a targeted inline entry exists (`hasInlineSection`) →
 *     nothing. SFCC-11656: a targeted `pdp-inline-content` entry supersedes
 *     the whole BM feature-content block, module 2 included. Every entry
 *     authored before COM-793 lands here (none has the field), so their PDPs
 *     keep looking exactly as they did — module 2's HTML was typically copied
 *     into the entry's canvas, and showing both would duplicate it and re-run
 *     its embed script on soft navigation.
 *  3. Otherwise → BM `c_module2FeatureContent` (within its display window).
 */
export function InlineBelowAccordionsSlot({
  region,
  hasInlineSection,
  product,
  getMasterId,
}: {
  /** `selectInlineBelowAccordions(pdpInlineSection)` — the projected region. */
  region: BuilderContent | null;
  /** Whether a targeted `pdp-inline-content` entry exists at all. */
  hasInlineSection: boolean;
  product: Promise<unknown> | unknown;
  /** Master-id key so a PDP→PDP soft nav re-runs the module script revival (SFCC-11655). */
  getMasterId: (product: unknown) => string;
}) {
  if (region) {
    return (
      <div
        key={region.id ?? "pdp-inline-below-accordions"}
        className="pt-5"
        data-testid="pdp-inline-content-below-accordions-section"
      >
        {/* Section-level Suspense so a suspending Builder custom component is
            contained here and can't bubble past the route <Outlet> into a
            page-level skeleton. */}
        <Suspense fallback={null}>
          <PrefetchedBuilderContent
            model={ModelMap.pdpInlineContent}
            content={region}
            canTrack={false}
          />
        </Suspense>
      </div>
    );
  }

  if (hasInlineSection) return null;

  return (
    <Suspense fallback={null}>
      <Await resolve={product}>
        {resolved => (
          <FeatureContentModules
            key={getMasterId(resolved)}
            product={resolved as Record<string, unknown>}
            includedKeys={["2"]}
            testId="feature-content-modules-below-accordions"
          />
        )}
      </Await>
    </Suspense>
  );
}
